import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { CLIENT_DASHBOARD_PATH } from "../../routes";

import WithAppLayout from "../../components/WithAppLayout";

import { LoadingIndicator } from "../../components/LoadingIndicator";
import {
  Stack,
  Box,
  SimpleGrid,
  Text,
  HStack,
} from "@chakra-ui/react";
import PageTitle from "../../components/PageTitle";
import { AGENCY_ANALYTICS_JOB_QUERY } from "../../graphql/analytics";
import WithQuery from "../../components/WithQuery";
import StatCard from "../../components/common/StatCard";

import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
const ONE_DAY_IN_S = 24 * 60 * 60;
const ONE_DAY_IN_MS = ONE_DAY_IN_S * 1000;

function InnerPage({
  data,
  error,
  loading,
  refetch,
  currentUser,
}) {
  if (loading) {
    return <LoadingIndicator />;
  }

  // Auto redirect to client dashboard page if client
  if (currentUser?.role === "Client") {
    return (
      <Navigate
        to={{
          pathname: CLIENT_DASHBOARD_PATH,
        }}
      />
    );
  }

  // Auto redirect to uncurated jobs page if recruiter
  if (currentUser?.role !== "Recruiter") {
    return null;
  }

  const thisData = data?.getAnalyticJobData;
  const prevData = data?.getAnalyticJobData.previous;

  const getTrend = (current, previous) => {
    if (current === undefined) current = 0;
    if (previous === undefined) previous = 0;
    const trend = current - previous;
    return trend >= 0 ? {isDown: false, text: `${trend} more`} : {isDown: true, text: `${Math.abs(trend)} less`};
  }
  const getAvgDayTrend = (current, previous) => {
    if (current === undefined) current = 0;
    if (previous === undefined) previous = 0;
    const trend = current - previous;
    const formattedTrend = (trend / (trend < 0 ? -ONE_DAY_IN_S : ONE_DAY_IN_S)).toFixed(1);
    return trend <= 0 ? {isDown: false, text: `${formattedTrend} days faster`} : {isDown: true, text: `${formattedTrend} days slower`};
  }
  const getDisplay = (val, place = 0, unit = '') => {
    if (val === undefined) val = 0;
    if (unit) unit = " " + unit;
    if (place === 0) {
      return `${Number(val).toLocaleString()}${unit}`;
    }
    return `${Number(val).toLocaleString(undefined, { minimumFractionDigits: place, maximumFractionDigits: place })}${unit}`;
  }
  const toHiredChartData = (data, startDate) => {
    if (!data) return [];
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    let timestamp = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1).getTime();
    if (startDate) {
      try {
        timestamp = new Date(startDate).getTime();
      } catch (e) {
        console.error("Invalid start date:", startDate, e);
      }
    }
    return data.map((item, index) => ({
      name: new Date(timestamp + index * ONE_DAY_IN_MS).toLocaleDateString('en-UK', { month: 'short', day: 'numeric' }),
      Hires: item,
    }));
  }

  return (
    <Stack>
      <PageTitle title={`Welcome Back ${currentUser?.firstName} ${currentUser?.lastName}!`} />
      <Text color="whiteAlpha.700" fontSize="lg" mb={4}>
        Last month's summary
      </Text>
      <Stack spacing={6}>
        <SimpleGrid columns={{md: 2, lg: 3}} spacingX="20px" spacingY="10px">
          <StatCard
            label="Jobs published"
            value={getDisplay(thisData?.jobPublished)}
            trend={getTrend(thisData?.jobPublished, prevData?.jobPublished)}
          />
          <StatCard
            label="Avg. time to hire"
            value={getDisplay((thisData?.avgHireTime ? thisData.avgHireTime/ONE_DAY_IN_S : 0), 1, 'days')}
            trend={getAvgDayTrend(thisData?.avgHireTime, prevData?.avgHireTime)}
          />
          <StatCard
            label="Interested candidates"
            value={getDisplay(thisData?.interested)}
            trend={getTrend(thisData?.interested, prevData?.interested)}
          />
          <StatCard
            label="Shortlisted candidates"
            value={getDisplay(thisData?.shortlisted)}
            trend={getTrend(thisData?.shortlisted, prevData?.shortlisted)}
          />
          <StatCard
            label="Approved candidates"
            value={getDisplay(thisData?.approved)}
            trend={getTrend(thisData?.approved, prevData?.approved)}
          />
          <StatCard
            label="Hired candidates"
            value={getDisplay(thisData?.hired)}
            trend={getTrend(thisData?.hired, prevData?.hired)}
          />
        </SimpleGrid>
        <Stack
          bg="black"
          borderRadius="16px"
          p={5}
          minW="260px"
          border="1px solid"
          borderColor="whiteAlpha.400"
          boxShadow="sm"
          position="relative"
        >
          <HStack>
            <Text color="whiteAlpha.900" fontSize="3xl" fontWeight="bold">
              {getDisplay(thisData?.hired)}
            </Text>
            <Text color="whiteAlpha.700" fontSize="lg" fontWeight="medium">
              Hires this month
            </Text>
          </HStack>
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart
              width={500}
              height={400}
              data={toHiredChartData(thisData?.dailyHireds, thisData?.startDate)}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0,
              }}
            >
              {/* Gradient definition */}
              <defs>
                <linearGradient id="colorHireds" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3C9084" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#3C9084" stopOpacity={0} />
                </linearGradient>
              </defs>
              {/* Remove CartesianGrid */}
              <XAxis dataKey="name"
                tick={{ fill: "#cbd5e1" }}
                tickLine={false}
                axisLine={{ stroke: "#cbd5e1" }}
               />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "none", borderRadius: 8, color: "#fff" }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#3C9084" }}
              />
              <Area
                type="monotone"
                dataKey="Hires"
                stroke="#3C9084"
                strokeWidth={3}
                fill="url(#colorHireds)"
                dot={{ r: 3, stroke: "#3C9084", strokeWidth: 2, fill: "#fff" }}
                activeDot={{ r: 6, fill: "#3C9084", stroke: "#fff", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Stack>
        <Stack pb={8}>
          <Text color="whiteAlpha.900" fontSize="3xl" fontWeight="bold" mb={4}>
            Top performing roles
          </Text>
          <Box overflowX="auto"
            bg="black"
            borderRadius="16px"
            p={5}
            minW="260px"
            border="1px solid"
            borderColor="whiteAlpha.400"
            boxShadow="sm">
            <table style={{ color: "#fff", width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ width: "25%", textAlign: "left", padding: "8px" }}>Job title</th>
                  <th style={{ width: "25%", textAlign: "right", padding: "8px" }}>Interested</th>
                  <th style={{ width: "25%", textAlign: "right", padding: "8px" }}>Shortlisted</th>
                  <th style={{ width: "25%", textAlign: "right", padding: "8px" }}>Hired</th>
                </tr>
              </thead>
              <tbody>
                {(thisData?.topRoles || []).map((role, idx) => (
                  <React.Fragment key={role.id || idx}>
                    <tr>
                      <td style={{ padding: "8px" }}>{role.name}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>{role.interested}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>{role.shortlisted}</td>
                      <td style={{ textAlign: "right", padding: "8px" }}>{role.hired}</td>
                    </tr>
                    <tr>
                      <td colSpan={4}>
                        <div style={{ borderBottom: "1px solid #222" }} />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

function PageComponent({ currentUser, children }) {
  const now = new Date();

  const location = useLocation();
  const queryString = location.search; // This gives you the query string, e.g. "?foo=bar"
  const params = new URLSearchParams(queryString);
  const year = params.get('year') ? parseInt(params.get('year')) : now.getFullYear();
  const month = params.get('month') ? parseInt(params.get('month')) : (now.getMonth() === 0 ? 12 : now.getMonth());
  const adjustedYear = params.get('month') ? year : (now.getMonth() === 0 ? year - 1 : year);
  return WithQuery(
    InnerPage,
    AGENCY_ANALYTICS_JOB_QUERY,
    { year: adjustedYear, month },
    {
      currentUser,
      children,
    }
  )();
}

export default WithAppLayout(PageComponent);
